import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getChatErrorMessage } from '@/features/chat/chat-errors';
import { checkDocumentNameConflicts, documentQueryKey, uploadIndexedDocument } from '../services/documents.api';
import { partitionDepositFiles, type DepositNameConflict } from '../lib/deposit-name-conflict';
import { partitionFiles } from '../lib/folder-traversal';

const MAX_PARALLEL_UPLOADS = 3;
const CANCELLED_DEPOSIT_NOTICE_MS = 6_000;
const CANCELLED_DEPOSIT_FADE_MS = 500;

type UploadQueueItemStatus = 'queued' | 'uploading' | 'uploaded' | 'failed' | 'cancelled';

type UploadQueueItem = {
  dismissing?: boolean;
  errorMessage?: string;
  id: string;
  name: string;
  replacedPrevious?: boolean;
  status: UploadQueueItemStatus;
};

function cancelledDepositFadeMs() {
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return 0;
  }
  return CANCELLED_DEPOSIT_FADE_MS;
}

export function useDocumentDeposits() {
  const queryClient = useQueryClient();
  const [replacePrompt, setReplacePrompt] = useState<{
    conflicts: DepositNameConflict[];
    files: File[];
  } | null>(null);
  const replaceChoice = useRef<'replace' | 'cancel' | null>(null);
  const [uploadQueue, setUploadQueue] = useState<UploadQueueItem[]>([]);
  const cancelledNoticeTimers = useRef(new Map<string, number>());

  useEffect(() => {
    const cancelledIds = new Set(
      uploadQueue.filter((item) => item.status === 'cancelled').map((item) => item.id),
    );
    const timers = cancelledNoticeTimers.current;

    for (const [id, timer] of timers) {
      if (!cancelledIds.has(id)) {
        window.clearTimeout(timer);
        timers.delete(id);
      }
    }

    for (const id of cancelledIds) {
      if (timers.has(id)) {
        continue;
      }
      timers.set(id, window.setTimeout(() => {
        const fadeMs = cancelledDepositFadeMs();
        if (fadeMs === 0) {
          timers.delete(id);
          setUploadQueue((current) => current.filter((entry) => entry.id !== id));
          return;
        }
        setUploadQueue((current) => current.map((entry) => (
          entry.id === id ? { ...entry, dismissing: true } : entry
        )));
        timers.set(id, window.setTimeout(() => {
          timers.delete(id);
          setUploadQueue((current) => current.filter((entry) => entry.id !== id));
        }, fadeMs));
      }, CANCELLED_DEPOSIT_NOTICE_MS));
    }
  }, [uploadQueue]);

  useEffect(() => () => {
    for (const timer of cancelledNoticeTimers.current.values()) {
      window.clearTimeout(timer);
    }
    cancelledNoticeTimers.current.clear();
  }, []);
  const isBatchUploading = uploadQueue.some((item) => item.status === 'queued' || item.status === 'uploading');

  function updateQueueItemStatus(itemId: string, updater: (item: UploadQueueItem) => UploadQueueItem) {
    setUploadQueue((current) => current.map((item) => (item.id === itemId ? updater(item) : item)));
  }

  async function runUploadQueue(files: File[], options?: { replace?: boolean }) {
    if (files.length === 0) {
      return;
    }

    const queueEntries: UploadQueueItem[] = files.map((file) => ({
      id: crypto.randomUUID(),
      name: file.name,
      status: 'queued',
    }));
    const entriesByIndex = queueEntries.map((entry, index) => ({ entry, file: files[index]! }));
    setUploadQueue((current) => [...entriesByIndex.map((item) => item.entry), ...current].slice(0, 20));

    let successCount = 0;
    let failureCount = 0;
    let replacedCount = 0;
    const failureMessages: string[] = [];
    let cursor = 0;

    async function runUploadWorker() {
      while (cursor < entriesByIndex.length) {
        const currentIndex = cursor;
        cursor += 1;
        const currentEntry = entriesByIndex[currentIndex];
        if (!currentEntry) {
          continue;
        }

        updateQueueItemStatus(currentEntry.entry.id, (item) => ({ ...item, status: 'uploading' }));
        try {
          const uploaded = await uploadIndexedDocument(currentEntry.file, { replace: options?.replace });
          successCount += 1;
          if (uploaded.replacedPrevious) {
            replacedCount += 1;
          }
          updateQueueItemStatus(currentEntry.entry.id, (item) => ({
            ...item,
            replacedPrevious: uploaded.replacedPrevious,
            status: 'uploaded',
          }));
        } catch (error) {
          failureCount += 1;
          const errorMessage = getChatErrorMessage(error);
          failureMessages.push(errorMessage);
          updateQueueItemStatus(currentEntry.entry.id, (item) => ({
            ...item,
            errorMessage,
            status: 'failed',
          }));
        }
      }
    }

    await Promise.all(Array.from({
      length: options?.replace ? 1 : Math.min(MAX_PARALLEL_UPLOADS, entriesByIndex.length),
    }, async () => {
      await runUploadWorker();
    }));

    await queryClient.invalidateQueries({ queryKey: documentQueryKey });

    if (successCount > 0 && failureCount === 0) {
      const sent = `${successCount} document${successCount > 1 ? 's' : ''} envoyé${successCount > 1 ? 's' : ''} pour indexation.`;
      toast.success(replacedCount === 0
        ? sent
        : replacedCount === successCount
          ? (replacedCount === 1
            ? 'Le document remplace la version précédente et part en indexation.'
            : `${replacedCount} documents remplacent la version précédente et partent en indexation.`)
          : `${sent} ${replacedCount} remplace${replacedCount > 1 ? 'nt' : ''} la version précédente.`);
      return;
    }

    if (failureCount === 1 && successCount === 0) {
      toast.error(failureMessages[0] ?? 'Le dépôt a échoué.');
      return;
    }

    if (successCount > 0 && failureCount > 0) {
      toast.warning(`${successCount} succès, ${failureCount} échec${failureCount > 1 ? 's' : ''}. Le détail est affiché sous la zone de dépôt.`);
      return;
    }

    toast.error(`${failureCount} échecs. Le détail est affiché sous la zone de dépôt.`);
  }

  function rememberSkippedDeposits(files: File[], messageFor: (file: File) => string) {
    if (files.length === 0) {
      return;
    }
    const items: UploadQueueItem[] = files.map((file) => ({
      errorMessage: messageFor(file),
      id: crypto.randomUUID(),
      name: file.name,
      status: 'cancelled',
    }));
    setUploadQueue((current) => [...items, ...current].slice(0, 20));
  }

  function skipConflictingDeposits(files: File[], conflicts: DepositNameConflict[]) {
    const { manual, sharepoint } = partitionDepositFiles(files, conflicts);
    rememberSkippedDeposits(manual, () => 'Ajout annulé. Le document déjà présent est conservé.');
    rememberSkippedDeposits(sharepoint, () => 'Ajout annulé. Ce nom est déjà utilisé par un document SharePoint.');
  }

  async function confirmReplacement(files: File[], conflicts: DepositNameConflict[]) {
    const { manual, sharepoint } = partitionDepositFiles(files, conflicts);
    skipConflictingDeposits(sharepoint, conflicts);
    await runUploadQueue(manual, { replace: true });
  }

  async function processFiles(rawFiles: File[]) {
    if (rawFiles.length === 0) {
      return;
    }

    const { acceptedFiles, ignoredCount: ignored } = partitionFiles(rawFiles);

    if (acceptedFiles.length === 0) {
      if (ignored > 0) {
        toast.warning(`Aucun fichier compatible trouvé. ${ignored} fichier(s) ignoré(s).`);
      }
      return;
    }

    if (ignored > 0) {
      toast.info(`${ignored} fichier(s) ignoré(s) (format non supporté).`);
    }

    const conflicts: DepositNameConflict[] = [];
    try {
      for (let index = 0; index < acceptedFiles.length; index += 100) {
        const slice = acceptedFiles.slice(index, index + 100);
        conflicts.push(...await checkDocumentNameConflicts(slice.map((file) => file.name)));
      }
    } catch (error) {
      toast.error(getChatErrorMessage(error));
      return;
    }

    const { fresh, manual, sharepoint } = partitionDepositFiles(acceptedFiles, conflicts);
    if (fresh.length > 0) {
      await runUploadQueue(fresh);
    }
    if (manual.length > 0 || sharepoint.length > 0) {
      setReplacePrompt({ conflicts, files: [...manual, ...sharepoint] });
    }
  }

  function chooseReplacement(choice: 'replace' | 'cancel') {
    replaceChoice.current = choice;
  }

  function onReplacementOpenChange(open: boolean) {
    if (open || !replacePrompt) return;
    const choice = replaceChoice.current;
    const prompt = replacePrompt;
    replaceChoice.current = null;
    setReplacePrompt(null);
    if (choice === 'replace') {
      void confirmReplacement(prompt.files, prompt.conflicts);
    } else {
      skipConflictingDeposits(prompt.files, prompt.conflicts);
    }
  }

  return { isBatchUploading, processFiles, replacePrompt, onReplacementOpenChange, chooseReplacement, uploadQueue };
}
